import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { getMassSchedules } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Church, Clock, Mail, Phone, MapPin, Users } from 'lucide-react';
import TranslatedContent from '../components/TranslatedContent';
import { AutoTranslate, AutoT } from '../hooks/useAutoTranslate';
import usePageBanner from '../hooks/usePageBanner';

const ChurchesPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [massSchedules, setMassSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('churches', language);

  useEffect(() => {
    loadMassSchedules();
  }, []);

  const loadMassSchedules = async () => {
    try {
      const schedules = await getMassSchedules();
      setMassSchedules(schedules);
    } catch (error) {
      console.error('Error loading mass schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get mass schedules for a church
  const getChurchMassSchedules = (churchCode) => {
    const dayOrder = {
      'sunday': 0,
      'monday': 1,
      'tuesday': 2,
      'wednesday': 3,
      'thursday': 4,
      'friday': 5,
      'saturday': 6
    };
    
    const massTypeTranslations = {
      'regular': t('mass'),
      'family': language === 'sv' ? 'Familjemässa' : language === 'en' ? 'Family Mass' : t('mass'),
      'youth': language === 'sv' ? 'Ungdomsmässa' : language === 'en' ? 'Youth Mass' : t('mass')
    };
    
    return massSchedules
      .filter(schedule => schedule.church === churchCode && schedule.active !== false)
      .sort((a, b) => {
        const dayDiff = dayOrder[a.day_of_week] - dayOrder[b.day_of_week];
        if (dayDiff !== 0) return dayDiff;
        return a.time.localeCompare(b.time);
      })
      .map(schedule => {
        const dayName = t(schedule.day_of_week);
        const massType = massTypeTranslations[schedule.mass_type] || schedule.mass_type;
        return {
          day: dayName,
          time: schedule.time,
          type: massType,
          language: schedule.language,
          details: schedule.details
        };
      });
  };

  const churches = [
    {
      name: 'Vår Fru av Fatima kyrka',
      location: 'Karlskrona',
      code: 'karlskrona',
      address: 'Södra Kungsgatan 1, 371 31 Karlskrona',
      image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/hbbpp13g_Fatimakrona.jpg',
      phone: '070-649 91 43',
      email: 'karlskrona@katolskakyrkan.se',
      pastor: 'Wojciech Waligórski',
      pastorPhone: '070-649 91 43',
      openingHours: { sv: 'Tis, Ons, Tor kl. 8.30-10.30' },
      masses: getChurchMassSchedules('karlskrona'),
      activities: [
        { sv: 'Bibelstudiegrupp' }, 
        { sv: 'Rosenkrans (onsdagar kl. 11.00)' }, 
        { sv: 'Bikt före mässorna' }
      ],
    },
    {
      name: 'S:t Bonifatius kyrka',
      location: 'Karlshamn',
      code: 'karlshamn',
      address: 'Norra Fogdelyckegatain 26, 374 38 Karlshamn',
      image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/1ttzserr_St%20Bonifatius.jpg',
      phone: '076-201 77 67',
      email: 'karlshamn@fatimakrona.se',
      masses: getChurchMassSchedules('karlshamn'),
      activities: [
        { sv: 'Barnkör' }, 
        { sv: 'Kvinnogrupp' }, 
        { sv: 'Välgörenhetsarbete' }
      ],
    },
    {
      name: 'S:t Antonius kyrka',
      location: 'Olofström',
      code: 'olofstrom',
      address: 'Bruksgatan 8, 293 38 Olofström',
      image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/fbsfqxbg_At%20Antonius.jpg',
      phone: '073-396 84 82',
      email: 'olofstrom@fatimakrona.se',
      pastor: 'Fr. Raphael Kurian (Subsidiar)',
      pastorPhone: '073-396 84 82',
      masses: getChurchMassSchedules('olofstrom'),
      activities: [
        { sv: 'Rosenkransgrupp' }, 
        { sv: 'Seniorklubb' }, 
        { sv: 'Matbank' }
      ],
    },
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage}
          alt="Churches"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle ? (
                <TranslatedContent content={{ sv: bannerTitle }} as="span" />
              ) : (
                <AutoTranslate>Våra kyrkor</AutoTranslate>
              )}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle ? (
                <TranslatedContent content={{ sv: bannerSubtitle }} as="span" />
              ) : (
                <AutoTranslate>Tre kyrkor - en gemenskap</AutoTranslate>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Historical Background */}
      <section className="py-12 bg-gradient-to-r from-amber-50 to-yellow-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl card-hover">
            <CardContent className="p-8">
              <h2 className={`text-2xl font-serif font-bold text-gray-900 mb-6 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Church className="w-8 h-8 text-yellow-600" />
                <AutoTranslate>Historisk bakgrund</AutoTranslate>
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="mb-4">
                  <AutoTranslate>
                    Församlingens namn har att göra med en liten by i Portugal som heter Fatima. I denna by visade sig Jungfru Maria för tre barn mellan 13 maj och 13 oktober år 1917. Uppenbarelsen till barnen har undersökts noggrant och bekräftats av Vatikanen.
                  </AutoTranslate>
                </p>
                <p className="mb-4">
                  <AutoTranslate>
                    Huvudbudskapet till barnen var att världen behöver bot, bön och omvändelse. Rosenkransbönens betydelse lyftes fram samt värdet av Eukaristin.
                  </AutoTranslate>
                </p>
                <p className="mb-4">
                  <AutoTranslate>Från budskapet i Fatima har vi sedan dess detta tillägg i Rosenkransbönen:</AutoTranslate>
                </p>
                <blockquote className={`${isRTL ? 'border-r-4 border-l-0 pr-6' : 'border-l-4 pl-6'} border-yellow-500 italic text-gray-800 bg-yellow-50 py-4 rounded-r-lg`}>
                  <AutoTranslate>
                    O min Jesus förlåt oss våra synder, bevara oss från helvetets eld, led alla själar till himmelen, särskilt dem som behöver din barmhärtighet allra mest.
                  </AutoTranslate>
                </blockquote>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Churches */}
      <section className="py-20" data-testid="churches-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {churches.map((church, index) => (
            <Card
              key={church.code}
              className={`overflow-hidden border-0 shadow-2xl fade-in ${
                index % 2 === 0 ? '' : 'bg-gradient-to-br from-amber-50 to-white'
              }`}
              data-testid={`church-${church.code}`}
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${
                index % 2 === 0 ? '' : 'lg:grid-flow-dense'
              }`}>
                {/* Image */}
                <div className={`h-96 lg:h-auto ${index % 2 === 0 ? '' : 'lg:col-start-2'}`}>
                  <img
                    src={church.image}
                    alt={church.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center space-x-3 mb-6">
                    <Church className="w-10 h-10 text-yellow-600" />
                    <div>
                      <h2 className="text-3xl font-serif font-bold text-gray-900">{church.name}</h2>
                      <p className="text-lg text-gray-600">{church.location}</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <MapPin className="w-5 h-5 text-gray-500" />
                      <span>{church.address}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <span>{church.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <span>{church.email}</span>
                    </div>
                    {church.pastor && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900"><AutoT tKey="churchPastor" /></p>
                        <p className="text-sm text-gray-700">{church.pastor}</p>
                        {church.pastorPhone && (
                          <p className="text-sm text-gray-600"><AutoT tKey="phone" />: {church.pastorPhone}</p>
                        )}
                      </div>
                    )}
                    {church.openingHours && (
                      <div className="mt-2 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900"><AutoT tKey="officeHours" /></p>
                        <TranslatedContent
                          content={church.openingHours}
                          as="p"
                          className="text-sm text-gray-700"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChurchesPage;
